import React, { useState, useEffect } from 'react';
import { Upload, FileText, Download, Trash2, Plus, File } from 'lucide-react';
import './MyDocuments.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyDocuments = ({ currentUser }) => {
  const [documents, setDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);

  useEffect(() => {
    if (currentUser?.email) {
      fetchDocuments();
    }
  }, [currentUser]);

  const fetchDocuments = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/documents/my-documents/${currentUser.email}`
      );
      const data = await response.json();
      setDocuments(data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setIsUploading(true);

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result;

        const response = await fetch(`${BACKEND_URL}/api/documents/upload-document`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            employee_email: currentUser.email,
            document_name: file.name,
            document_type: file.type,
            file_size: file.size,
            file_data: base64Data
          })
        });

        if (!response.ok) throw new Error('Upload failed');

        const result = await response.json();
        alert(`✅ ${result.document_name} uploaded successfully!`);
        
        // Refresh documents list
        fetchDocuments();
        setShowUploadForm(false);
      };

      reader.readAsDataURL(file);

    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (document) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/documents/download-document/${document.id}`
      );
      const data = await response.json();

      // Create download link
      const link = document.createElement('a');
      link.href = data.file_data;
      link.download = data.document_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document.');
    }
  };

  const handleDelete = async (document) => {
    if (!window.confirm(`Are you sure you want to delete "${document.document_name}"?`)) {
      return;
    }

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/documents/delete-document/${document.id}?employee_email=${currentUser.email}`,
        { method: 'DELETE' }
      );

      if (!response.ok) throw new Error('Delete failed');

      alert('✅ Document deleted successfully!');
      fetchDocuments();

    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document.');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getFileIcon = (docType) => {
    if (docType.includes('pdf')) return '📄';
    if (docType.includes('image')) return '🖼️';
    if (docType.includes('word') || docType.includes('document')) return '📝';
    if (docType.includes('excel') || docType.includes('sheet')) return '📊';
    return '📎';
  };

  return (
    <div className="my-documents-container">
      <div className="documents-header">
        <div>
          <h2>My Documents</h2>
          <p>Store your licenses, certifications, and important documents</p>
        </div>
        <button 
          className="add-document-btn"
          onClick={() => setShowUploadForm(!showUploadForm)}
        >
          <Plus size={18} /> Add Document
        </button>
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <div className="upload-form-card">
          <h3>
            <Upload size={20} /> Upload New Document
          </h3>
          <p>Upload licenses, certifications, or any important documents (Max 10MB)</p>
          
          <div className="upload-zone">
            <input
              type="file"
              id="file-upload"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              disabled={isUploading}
            />
            <label htmlFor="file-upload" className={isUploading ? 'uploading' : ''}>
              {isUploading ? (
                <>⏳ Uploading...</>
              ) : (
                <>
                  <File size={32} />
                  <span>Click to select file or drag and drop</span>
                  <small>PDF, DOC, DOCX, JPG, PNG (Max 10MB)</small>
                </>
              )}
            </label>
          </div>
        </div>
      )}

      {/* Documents List */}
      <div className="documents-grid">
        {documents.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} />
            <h3>No Documents Yet</h3>
            <p>Click "Add Document" to upload your first document</p>
          </div>
        ) : (
          documents.map((doc) => (
            <div key={doc.id} className="document-card">
              <div className="document-icon">
                {getFileIcon(doc.document_type)}
              </div>
              <div className="document-info">
                <h4>{doc.document_name}</h4>
                <div className="document-meta">
                  <span>{formatFileSize(doc.file_size)}</span>
                  <span>•</span>
                  <span>{new Date(doc.upload_date).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="document-actions">
                <button
                  className="download-btn"
                  onClick={() => handleDownload(doc)}
                  title="Download"
                >
                  <Download size={18} />
                </button>
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(doc)}
                  title="Delete"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {documents.length > 0 && (
        <div className="documents-summary">
          <p>Total documents: <strong>{documents.length}</strong></p>
        </div>
      )}
    </div>
  );
};

export default MyDocuments;
